import { useCallback, useEffect, useMemo, useState } from 'react';
import { call } from '../lib/backbarApi';
import { Empty, ErrorNote, PageHead } from '../components/ui';
import {
  blockFor,
  loadModuleDoc,
  loadModuleTitles,
  loadRows,
  type ImageRow,
  type ImageStatus,
  type ModuleDoc,
} from '../components/illustrations/data';
import { IllustrationCard } from '../components/illustrations/IllustrationCard';
import { IllustrationDrawer } from '../components/illustrations/IllustrationDrawer';
import '../components/illustrations/illustrations.css';

type Chip = 'all' | ImageStatus;
const CHIPS: Array<{ key: Chip; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'missing', label: 'Missing' },
  { key: 'redo', label: 'Marked for redo' },
  { key: 'placed', label: 'Placed' },
];

type WorkOrder = { markdown: string; count: number };

export function Illustrations() {
  const [rows, setRows] = useState<ImageRow[] | null>(null);
  const [titles, setTitles] = useState<Map<string, { title: string; sort: number }>>(new Map());
  const [docs, setDocs] = useState<Map<string, ModuleDoc>>(new Map());
  const [loadError, setLoadError] = useState<unknown>(null);
  const [chip, setChip] = useState<Chip>('all');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  /** filename → cache-buster after a fresh upload, so the thumbnail refreshes. */
  const [versions, setVersions] = useState<Record<string, number>>({});
  const [orderBusy, setOrderBusy] = useState(false);
  const [orderError, setOrderError] = useState<unknown>(null);

  useEffect(() => {
    (async () => {
      try {
        const [r, t] = await Promise.all([loadRows(), loadModuleTitles()]);
        setRows(r);
        setTitles(t);
      } catch (err) {
        setLoadError(err);
      }
    })();
  }, []);

  // The published lesson documents carry the caption and brief for each
  // illustration; they arrive module by module after the list is up.
  useEffect(() => {
    if (!rows) return;
    const ids = [...new Set(rows.map((r) => r.module_id).filter((m): m is string => Boolean(m)))];
    let cancelled = false;
    (async () => {
      const queue = [...ids];
      const worker = async () => {
        while (queue.length && !cancelled) {
          const id = queue.shift()!;
          try {
            const doc = await loadModuleDoc(id);
            if (!cancelled) setDocs((d) => new Map(d).set(id, doc));
          } catch {
            // A module that fails to load leaves its captions blank; the card still opens.
          }
        }
      };
      await Promise.all([worker(), worker(), worker(), worker()]);
    })();
    return () => {
      cancelled = true;
    };
  }, [rows]);

  const counts = useMemo(() => {
    const c: Record<Chip, number> = { all: 0, placed: 0, missing: 0, redo: 0, retired: 0 };
    for (const r of rows ?? []) {
      c[r.status] += 1;
      if (r.status !== 'retired') c.all += 1;
    }
    return c;
  }, [rows]);

  const moduleTitle = useCallback(
    (r: ImageRow) => (r.module_id ? titles.get(r.module_id)?.title ?? docs.get(r.module_id)?.title ?? r.module_id : ''),
    [titles, docs],
  );

  const visible = useMemo(() => {
    if (!rows) return [];
    const needle = q.trim().toLowerCase();
    const list = rows.filter((r) => {
      if (chip === 'all' ? r.status === 'retired' && needle === '' : r.status !== chip) return false;
      if (!needle) return true;
      const block = blockFor(r, r.module_id ? docs.get(r.module_id) : undefined);
      const hay = [r.filename, r.module_id, r.lesson_id, moduleTitle(r), block?.lessonTitle, block?.caption]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
    const order = (r: ImageRow) => (r.module_id ? titles.get(r.module_id)?.sort ?? 999 : 1000);
    return list.sort((a, b) => order(a) - order(b) || (a.lesson_id ?? '').localeCompare(b.lesson_id ?? '') || a.filename.localeCompare(b.filename));
  }, [rows, chip, q, docs, titles, moduleTitle]);

  const openRow = open ? rows?.find((r) => r.filename === open) ?? null : null;

  function onRowChange(next: ImageRow, version?: string | number) {
    setRows((rs) => rs?.map((r) => (r.filename === next.filename ? { ...r, ...next } : r)) ?? rs);
    if (version != null) setVersions((v) => ({ ...v, [next.filename]: Number(version) }));
  }

  async function downloadWorkOrder() {
    setOrderBusy(true);
    setOrderError(null);
    try {
      const wo = await call<WorkOrder>('images.work_order');
      const date = new Date().toISOString().slice(0, 10);
      const blob = new Blob([wo.markdown], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `illustration-work-order-${date}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      setOrderError(err);
    } finally {
      setOrderBusy(false);
    }
  }

  return (
    <>
      <PageHead
        title="Illustrations"
        sub={
          rows
            ? `${counts.placed} placed, ${counts.missing} missing, ${counts.redo} marked for redo.`
            : 'Every picture in the curriculum, and the ones still to make.'
        }
        right={
          <button type="button" className="lp-btn" disabled={orderBusy || !rows} onClick={() => void downloadWorkOrder()}>
            {orderBusy ? 'Preparing…' : 'Download work order'}
          </button>
        }
      />
      <ErrorNote error={loadError} />
      <ErrorNote error={orderError} />

      <div className="il-toolbar">
        <div className="il-chips" role="tablist" aria-label="Show">
          {CHIPS.map((c) => (
            <button
              key={c.key}
              type="button"
              role="tab"
              aria-selected={chip === c.key}
              className={chip === c.key ? 'il-chip on' : 'il-chip'}
              onClick={() => setChip(c.key)}
            >
              {c.label}
              {rows ? <b>{counts[c.key]}</b> : null}
            </button>
          ))}
        </div>
        <input
          className="lp-inp lp-inp--search"
          type="search"
          placeholder="Search by file name, module, lesson, or caption"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search illustrations"
        />
        {rows && <span className="il-count">{visible.length} shown</span>}
      </div>

      {!rows && !loadError && <Empty>Loading…</Empty>}
      {rows && visible.length === 0 && <Empty>Nothing matches.</Empty>}

      <div className="il-grid">
        {visible.map((r) => (
          <IllustrationCard
            key={r.filename}
            row={r}
            moduleTitle={moduleTitle(r)}
            block={blockFor(r, r.module_id ? docs.get(r.module_id) : undefined)}
            version={versions[r.filename]}
            onOpen={(row) => setOpen(row.filename)}
          />
        ))}
      </div>

      {openRow && (
        <IllustrationDrawer
          key={openRow.filename}
          row={openRow}
          moduleTitle={moduleTitle(openRow)}
          block={blockFor(openRow, openRow.module_id ? docs.get(openRow.module_id) : undefined)}
          version={versions[openRow.filename]}
          onClose={() => setOpen(null)}
          onRowChange={onRowChange}
        />
      )}
    </>
  );
}
